// src/App.tsx
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Hourglass } from 'lucide-react';

// Import utilities and services
import {
  generateSessionId,
  extractLocation,
  hasRaceStarted,
  getLiquiditySymbol,
} from './utils';
import { fetchJockeyRaces } from './apiService';
import {
  getTrueOddsLogic,
  getMidpointOddsLogic,
  getAdjustedBackOddsLogic,
  calculateEVLogic,
} from './oddsCalculations';
import { processTimeSeriesData } from './chartUtils';
import MetricsDisplay from './components/MetricsDisplay';
import ResultsTable from './components/ResultsTable';
import SearchForm from './components/SearchForm';
import SessionNavigationBar from './components/SessionNavigationBar';
import SavedSessionCard from './components/SavedSessionCard';
import SportAndEvaluationSelection, {
  SelectableItem,
} from './components/SportAndEvaluationSelection';
import SystemControlsBar from './components/SystemControlsBar';
import AnimatedBackground from './components/ui/AnimatedBackground';
import { ContentContainer } from './components/ui/ContentContainer';

// Import admin components
import AdminAuth from './components/AdminAuth';
import AdminConsole from './components/AdminConsole';

// Import iOS install prompt
import IOSInstallPrompt from './components/IOSInstallPrompt';
import { useIOSInstallPrompt } from './hooks/useIOSInstallPrompt';

// Import types from the new central types file
import {
  RaceResult,
  ApiResponse,
  JockeyRacesRequestPayload,
  EVHistoryEntry,
  LiquidityHistoryEntry,
  BackOddsHistoryEntry,
  SessionData,
  ProcessedRaceRow,
  EventGroup,
  CurrentInstanceData,
} from './types';

// Import watchlist store
import {
  useWatchlistStore,
  useWatchlistCleanup,
} from './stores/watchlistStore';

// --- CONSTANTS ---
const LOADING_MESSAGES = [
  "Hold your horses! <br /> We're rounding up the odds just for you.",
  "Even horses need a breather. <br /> We'll be with you shortly!",
  'Calculating odds faster than a thoroughbred out of the gate!',
  "We're off to the races! <br /> Please hold while we fetch those odds.",
  "Your odds are being groomed to perfection. <br /> We'll be ready in a jiffy!",
  "We're on the final stretch! Your odds will be ready in a jiffy!!",
];
const REFRESH_CYCLE_AUTO = 15; // seconds
const REFRESH_CYCLE_LIVE = 2; // seconds

// Placeholder SVG Icons
const HorseIcon = () => (
  <svg viewBox='0 0 100 100' width='40' height='40' fill='currentColor'>
    <path d='M50 10 C 40 10, 20 20, 20 40 C 20 60, 30 70, 35 80 L 40 75 C 40 70, 45 60, 50 60 C 55 60, 60 70, 60 75 L 65 80 C 70 70, 80 60, 80 40 C 80 20, 60 10, 50 10 Z' />
    <circle cx='30' cy='40' r='3' />
    <circle cx='70' cy='40' r='3' />
    <path d='M45 85 L 40 95 L 60 95 L 55 85 Z' />
  </svg>
);
const GreyhoundIcon = () => (
  <svg viewBox='0 0 100 100' width='40' height='40' fill='currentColor'>
    <path d='M65,20 A15,15 0 0,0 50,30 L20,30 L20,40 L40,40 L40,60 L30,70 L40,80 L60,80 L70,70 L60,60 L60,45 A10,10 0 0,1 70,35 L80,35 L80,25 L70,20z M50,10 L55,20 L45,20z' />
  </svg>
);
const JockeyIcon = () => (
  <svg viewBox='0 0 100 100' width='40' height='40' fill='currentColor'>
    <circle cx='50' cy='30' r='15' />
    <path d='M35 50 C 35 40, 40 40, 50 40 C 60 40, 65 40, 65 50 L 65 70 L 55 90 L 45 90 L 35 70 Z' />
  </svg>
);
const TrainerIcon = () => (
  <svg viewBox='0 0 100 100' width='40' height='40' fill='currentColor'>
    <text
      x='50%'
      y='50%'
      dominantBaseline='middle'
      textAnchor='middle'
      fontSize='50'
      fontWeight='bold'
    >
      T
    </text>
  </svg>
);
const HorseProfileIcon = () => (
  <svg viewBox='0 0 100 100' width='40' height='40' fill='currentColor'>
    <text
      x='50%'
      y='50%'
      dominantBaseline='middle'
      textAnchor='middle'
      fontSize='50'
      fontWeight='bold'
    >
      H
    </text>
  </svg>
);

function App() {
  // --- STATE DECLARATIONS ---
  const [selectedSportType, setSelectedSportType] = useState<string | null>(
    'horse-racing'
  );
  const [selectedEvaluationType, setSelectedEvaluationType] = useState<
    string | null
  >('jockey');

  const sportTypes: SelectableItem[] = useMemo(
    () => [
      { id: 'horse-racing', name: 'Horse Racing', icon: <HorseIcon /> },
      {
        id: 'greyhound-racing',
        name: 'Greyhounds',
        icon: <GreyhoundIcon />,
        isComingSoon: true,
      },
    ],
    []
  );

  const evaluationTypes: Record<string, SelectableItem[]> = useMemo(
    () => ({
      'horse-racing': [
        { id: 'jockey', name: 'Jockey', icon: <JockeyIcon /> },
        {
          id: 'trainer',
          name: 'Trainer',
          icon: <TrainerIcon />,
          isComingSoon: true,
        },
        {
          id: 'horse',
          name: 'Horse',
          icon: <HorseProfileIcon />,
          isComingSoon: true,
        },
      ],
      'greyhound-racing': [
        { id: 'dog', name: 'Dog', icon: <GreyhoundIcon />, isComingSoon: true },
        {
          id: 'trainer',
          name: 'Trainer',
          icon: <TrainerIcon />,
          isComingSoon: true,
        },
      ],
    }),
    []
  );

  useEffect(() => {
    let newEvalType: string | null = null;
    if (selectedSportType && evaluationTypes[selectedSportType]) {
      const currentSportEvalTypes = evaluationTypes[selectedSportType];
      const currentEvalIsValid = currentSportEvalTypes.some(
        (et) => et.id === selectedEvaluationType && !et.isComingSoon
      );

      if (!currentEvalIsValid) {
        const firstAvailableEvalType = currentSportEvalTypes.find(
          (et) => !et.isComingSoon
        );
        newEvalType = firstAvailableEvalType ? firstAvailableEvalType.id : null;
      } else {
        newEvalType = selectedEvaluationType;
      }
    } else {
      newEvalType = null;
    }

    if (newEvalType !== selectedEvaluationType) {
      setSelectedEvaluationType(newEvalType);
    }
  }, [selectedSportType, selectedEvaluationType, evaluationTypes]);

  const refreshCycleSeconds = useRef(REFRESH_CYCLE_AUTO);

  const [loadingMessage, setLoadingMessage] = useState('');
  const [jockey1, setJockey1] = useState('');
  const [jockey2, setJockey2] = useState('');
  const [boostedOdds, setBoostedOdds] = useState('');
  const [racesToWin, setRacesToWin] = useState(2);
  const [results, setResults] = useState<RaceResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // If no saved theme, check system preference
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    );
  });
  const [toggleMode, setToggleMode] = useState<'static' | 'auto' | 'live'>(
    'auto'
  );
  const [isLive, setIsLive] = useState(false);
  const [systemOnline, setSystemOnline] = useState(true);
  const [refreshCount, setRefreshCount] = useState(0);

  const [evHistory, setEvHistory] = useState<EVHistoryEntry[]>([]);
  const [liquidityHistory, setLiquidityHistory] = useState<
    LiquidityHistoryEntry[]
  >([]);
  const [backOddsHistory, setBackOddsHistory] = useState<
    BackOddsHistoryEntry[]
  >([]);

  const [currentInstance, setCurrentInstance] =
    useState<CurrentInstanceData | null>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  const [timeSinceUpdate, setTimeSinceUpdate] = useState(0);
  const [lastSuccessfulUpdate, setLastSuccessfulUpdate] = useState<Date | null>(
    null
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [backOddsPercentage, setBackOddsPercentage] = useState(15);
  const [applyBackOddsPercentage, setApplyBackOddsPercentage] = useState(false);

  // Remove local savedSessions state - use Zustand store instead
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [hasSearchResults, setHasSearchResults] = useState(false);
  const [lastUpdateText, setLastUpdateText] = useState('');
  const [isStartingNewSearch, setIsStartingNewSearch] = useState(true);
  const [showAnimatedBackground, setShowAnimatedBackground] = useState(true);

  // Admin console state
  const [adminMode, setAdminMode] = useState<'none' | 'login' | 'console'>(
    'none'
  );

  // iOS install prompt state
  const { showPrompt: showIOSPrompt, hidePrompt: hideIOSPrompt } =
    useIOSInstallPrompt();

  // Zustand store - now the primary source of saved sessions
  const {
    savedSessions,
    addSession: zustandAddSession,
    removeSession: zustandRemoveSession,
    updateSession: zustandUpdateSession,
    getSessionById: zustandGetSessionById,
    visibleSessionIds: zustandVisibleSessionIds,
    toggleSessionVisibility: zustandToggleSessionVisibility,
    setActiveSession: zustandSetActiveSession,
    activeSessionId: zustandActiveSessionId,
  } = useWatchlistStore();

  // Initialize cleanup hook
  useWatchlistCleanup();

  // Force usage of all store variables to prevent tree-shaking
  useEffect(() => {
    // This ensures all Zustand store functions are included in the build
    if (
      typeof zustandGetSessionById === 'function' &&
      typeof zustandSetActiveSession === 'function' &&
      typeof zustandActiveSessionId === 'string'
    ) {
      // Store is properly loaded
    }
  }, [zustandGetSessionById, zustandSetActiveSession, zustandActiveSessionId]);

  // --- END STATE DECLARATIONS ---

  const selectSportTypeHandler = (sportId: string) => {
    setSelectedSportType(sportId);
  };

  const selectEvaluationTypeHandler = (evalId: string) => {
    setSelectedEvaluationType(evalId);
  };

  const getRandomLoadingMessage = useCallback(() => {
    return LOADING_MESSAGES[
      Math.floor(Math.random() * LOADING_MESSAGES.length)
    ];
  }, []);

  const calculateTotalLiquidity = useCallback(
    (rows: RaceResult[]): { symbol: string; value: number; total: number } => {
      if (!rows || rows.length === 0)
        return { symbol: '$', value: 0, total: 0 };
      let totalLiquidityValue = 0;
      let totalSymbolValue = 0;
      const activeRaceRows = rows.filter(
        (row) => !hasRaceStarted(row.marketStartTime)
      );
      activeRaceRows.forEach((row) => {
        totalLiquidityValue += row.liquidity || 0;
        totalSymbolValue += getLiquiditySymbol(row.liquidity).value;
      });
      const avgSymbolValue =
        activeRaceRows.length > 0
          ? Math.floor(totalSymbolValue / activeRaceRows.length)
          : 0;
      const symbol = '$'.repeat(avgSymbolValue || 1);
      return { symbol, value: avgSymbolValue, total: totalLiquidityValue };
    },
    []
  );

  const getTrueOddsForMainDisplay = useCallback(
    (
      resultsData: RaceResult[],
      racesToWinValue: number | string
    ): number | null => {
      return getTrueOddsLogic(resultsData, racesToWinValue);
    },
    []
  );

  const getMidpointOddsForMainDisplay = useCallback(
    (
      resultsData: RaceResult[],
      racesToWinValue: number | string
    ): number | null => {
      return getMidpointOddsLogic(resultsData, racesToWinValue);
    },
    []
  );

  const getAdjustedBackOddsForMainDisplay = useCallback(
    (
      resultsData: RaceResult[],
      racesToWinValue: number | string,
      currentBackOddsPercentage: number,
      currentApplyBackOddsPercentage: boolean
    ): number | null => {
      return getAdjustedBackOddsLogic(
        resultsData,
        racesToWinValue,
        currentBackOddsPercentage,
        currentApplyBackOddsPercentage
      );
    },
    []
  );

  const handleRefresh = useCallback(
    async (
      forceRefresh = false,
      sessionId: string | null = null,
      shouldManageGlobalState = true
    ) => {
      const targetSession = sessionId
        ? savedSessions.find((s) => s.id === sessionId)
        : null;
      const instanceToRefresh = targetSession
        ? targetSession.currentInstance
        : currentInstance;
      if (!instanceToRefresh) return;

      if (shouldManageGlobalState) {
        setIsRefreshing(true);
      }

      const refreshPayload: JockeyRacesRequestPayload = {
        ...instanceToRefresh,
        forceRefresh,
      };
      try {
        const data: ApiResponse = await fetchJockeyRaces(refreshPayload);
        const now = new Date();
        if (sessionId && targetSession) {
          const sessionState = {
            results: data.results,
            boostedOdds: targetSession.boostedOdds,
            racesToWin: targetSession.racesToWin,
            applyBackOddsPercentage: targetSession.applyBackOddsPercentage,
            backOddsPercentage: targetSession.backOddsPercentage,
          };
          const activeRaces = sessionState.results.filter(
            (r) => !hasRaceStarted(r.marketStartTime)
          );
          const sessionLayTrueOdds = getTrueOddsLogic(
            activeRaces,
            sessionState.racesToWin
          );
          const sessionMidpointOdds = getMidpointOddsLogic(
            activeRaces,
            sessionState.racesToWin
          );
          const layEV = calculateEVLogic(
            sessionLayTrueOdds,
            sessionState.boostedOdds
          );
          const midpointEV = calculateEVLogic(
            sessionMidpointOdds,
            sessionState.boostedOdds
          );
          const { total: totalLiquidity, value: liquidityRating } =
            calculateTotalLiquidity(activeRaces);
          let adjustedEVPercentage: number | null = null;
          if (sessionState.applyBackOddsPercentage) {
            const adjustedOdds = getAdjustedBackOddsLogic(
              activeRaces,
              sessionState.racesToWin,
              sessionState.backOddsPercentage,
              sessionState.applyBackOddsPercentage
            );
            const adjustedEV = adjustedOdds
              ? calculateEVLogic(adjustedOdds, sessionState.boostedOdds)
              : null;
            adjustedEVPercentage = adjustedEV
              ? Math.round(adjustedEV * 100)
              : null;
          }
          const updatedSessionPart: Partial<SessionData> = {
            results: data.results,
            lastUpdateTime: now,
          };
          if (activeRaces.length > 0) {
            updatedSessionPart.evHistory = [
              ...(targetSession.evHistory || []).slice(-19),
              {
                timestamp: now,
                layEV: layEV ? Math.round(layEV * 100) : null,
                midpointEV: midpointEV ? Math.round(midpointEV * 100) : null,
              },
            ];
            updatedSessionPart.liquidityHistory = [
              ...(targetSession.liquidityHistory || []).slice(-19),
              {
                timestamp: now,
                total: totalLiquidity,
                rating: liquidityRating,
              },
            ];
            if (
              sessionState.applyBackOddsPercentage &&
              adjustedEVPercentage !== null
            ) {
              updatedSessionPart.backOddsHistory = [
                ...(targetSession.backOddsHistory || []).slice(-19),
                { timestamp: now, adjustedEV: adjustedEVPercentage },
              ];
            } else if (
              sessionState.applyBackOddsPercentage &&
              adjustedEVPercentage === null
            ) {
              updatedSessionPart.backOddsHistory = [
                ...(targetSession.backOddsHistory || []).slice(-19),
                { timestamp: now, adjustedEV: null },
              ];
            } else {
              updatedSessionPart.backOddsHistory =
                targetSession.applyBackOddsPercentage
                  ? updatedSessionPart.backOddsHistory
                  : [];
            }
          } else {
            updatedSessionPart.evHistory = [];
            updatedSessionPart.liquidityHistory = [];
            updatedSessionPart.backOddsHistory = [];
          }

          zustandUpdateSession(sessionId, updatedSessionPart);

          if (activeSessionId === sessionId) {
            setResults(data.results);
            setEvHistory(updatedSessionPart.evHistory || []);
            setLiquidityHistory(updatedSessionPart.liquidityHistory || []);
            setBackOddsHistory(updatedSessionPart.backOddsHistory || []);
            setLastUpdateTime(now);
            setTimeSinceUpdate(0);
            setLastSuccessfulUpdate(now);
          }
        } else {
          setResults(data.results);
          setRefreshCount((prevCount) => prevCount + 1);
          setLastUpdateTime(now);
          setTimeSinceUpdate(0);
          setLastSuccessfulUpdate(now);
        }
        setSystemOnline(true);
        setError('');
      } catch (err: any) {
        console.error('Error during refresh:', err);
        setError(`Failed to fetch data: ${err.message || 'Unknown error'}`);
        setSystemOnline(false);
      } finally {
        if (shouldManageGlobalState) {
          setIsRefreshing(false);
        }
      }
    },
    [
      savedSessions,
      currentInstance,
      activeSessionId,
      calculateTotalLiquidity,
      zustandUpdateSession,
    ]
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (activeSessionId) return;
    if (
      selectedSportType !== 'horse-racing' ||
      selectedEvaluationType !== 'jockey'
    ) {
      setError('This sport/evaluation type is not yet implemented for search.');
      return;
    }

    setLoading(true);
    setLoadingMessage(getRandomLoadingMessage());
    setError('');

    const newInstancePayload: JockeyRacesRequestPayload = {
      jockeyNames: [jockey1, jockey2].filter(Boolean),
      odds: boostedOdds,
      racesToWin,
    };

    try {
      setCurrentInstance(newInstancePayload);
      const data: ApiResponse = await fetchJockeyRaces(newInstancePayload);
      const now = new Date();
      setResults(data.results);
      setSystemOnline(true);
      setLastSuccessfulUpdate(now);
      setLastUpdateTime(now);
      setTimeSinceUpdate(0);
      setHasSearchResults(true);
      setIsStartingNewSearch(false);
      const activeRaces = data.results.filter(
        (r) => !hasRaceStarted(r.marketStartTime)
      );
      if (activeRaces.length > 0) {
        const currentLayEV = calculateEVLogic(
          getTrueOddsLogic(activeRaces, newInstancePayload.racesToWin),
          newInstancePayload.odds
        );
        const currentMidpointEV = calculateEVLogic(
          getMidpointOddsLogic(activeRaces, newInstancePayload.racesToWin),
          newInstancePayload.odds
        );
        const { total: totalLiquidity, value: liquidityRating } =
          calculateTotalLiquidity(activeRaces);
        setEvHistory([
          {
            timestamp: now,
            layEV: currentLayEV ? Math.round(currentLayEV * 100) : null,
            midpointEV: currentMidpointEV
              ? Math.round(currentMidpointEV * 100)
              : null,
          },
        ]);
        setLiquidityHistory([
          { timestamp: now, total: totalLiquidity, rating: liquidityRating },
        ]);
        if (applyBackOddsPercentage) {
          const adjustedOdds = getAdjustedBackOddsLogic(
            activeRaces,
            newInstancePayload.racesToWin,
            backOddsPercentage,
            applyBackOddsPercentage
          );
          const adjustedEV = adjustedOdds
            ? calculateEVLogic(adjustedOdds, newInstancePayload.odds)
            : null;
          const adjustedEVPercentageValue = adjustedEV
            ? Math.round(adjustedEV * 100)
            : null;
          if (adjustedEVPercentageValue !== null) {
            setBackOddsHistory([
              { timestamp: now, adjustedEV: adjustedEVPercentageValue },
            ]);
          }
        } else {
          setBackOddsHistory([]);
        }
      }
    } catch (err: any) {
      console.error('Error during search:', err);
      setError(`Failed to fetch data: ${err.message || 'Unknown error'}`);
      setSystemOnline(false);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const root = window.document.documentElement;
    if (darkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    setIsLive(toggleMode !== 'static');
    refreshCycleSeconds.current =
      toggleMode === 'live' ? REFRESH_CYCLE_LIVE : REFRESH_CYCLE_AUTO;
  }, [toggleMode]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (isLive && lastSuccessfulUpdate && !isRefreshing) {
      const refreshLogic = () => {
        const secondsSinceLast = Math.floor(
          (new Date().getTime() - lastSuccessfulUpdate.getTime()) / 1000
        );
        setTimeSinceUpdate(secondsSinceLast);

        if (secondsSinceLast >= refreshCycleSeconds.current) {
          // Collect all sessions that need refreshing
          const liveSessions = savedSessions.filter(
            (session) => session.isLive
          );

          const refreshPromises = [];

          // Add main search session if it exists and is not active
          if (currentInstance && hasSearchResults && !activeSessionId) {
            refreshPromises.push(handleRefresh(false, null, false));
          }

          // Add all live saved sessions
          liveSessions.forEach((session) => {
            refreshPromises.push(handleRefresh(false, session.id, false));
          });

          if (refreshPromises.length > 0) {
            // Set refreshing state before starting all API calls
            setIsRefreshing(true);

            // Refresh all sessions simultaneously
            Promise.all(refreshPromises)
              .then(() => {
                // All refreshes completed successfully
                setIsRefreshing(false);
                setLastSuccessfulUpdate(new Date());
                setTimeSinceUpdate(0);
              })
              .catch((error) => {
                // Handle any refresh errors
                console.error('Error during coordinated refresh:', error);
                setIsRefreshing(false);
                setSystemOnline(false);
              });
          }
        }
      };
      refreshLogic();
      timer = setInterval(refreshLogic, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [
    isLive,
    lastSuccessfulUpdate,
    isRefreshing,
    activeSessionId,
    savedSessions,
    currentInstance,
    hasSearchResults,
    handleRefresh,
  ]);

  useEffect(() => {
    if (!lastSuccessfulUpdate) {
      setLastUpdateText('');
      return;
    }
    const updateText = () => {
      const now = new Date();
      const secondsAgo = Math.floor(
        (now.getTime() - lastSuccessfulUpdate.getTime()) / 1000
      );
      if (toggleMode === 'static') {
        if (secondsAgo < 60) setLastUpdateText(`Updated ${secondsAgo}s ago`);
        else if (secondsAgo < 3600)
          setLastUpdateText(`Updated ${Math.floor(secondsAgo / 60)}m ago`);
        else setLastUpdateText(`Updated ${Math.floor(secondsAgo / 3600)}h ago`);
      } else if (toggleMode === 'auto') {
        const secondsToNextRefresh =
          refreshCycleSeconds.current - timeSinceUpdate;
        if (isRefreshing) {
          setLastUpdateText('Refreshing...');
        } else if (secondsToNextRefresh > 0) {
          setLastUpdateText(`Next refresh in ${secondsToNextRefresh}s`);
        } else {
          setLastUpdateText(`Refreshing...`);
        }
      } else {
        setLastUpdateText('Live');
      }
    };
    updateText();
    const timer = setInterval(updateText, 1000);
    return () => clearInterval(timer);
  }, [lastSuccessfulUpdate, toggleMode, timeSinceUpdate, isRefreshing]);

  useEffect(() => {
    if (activeSessionId || !results.length || !currentInstance) {
      return;
    }

    const now = new Date();
    const activeRaces = results.filter(
      (r) => !hasRaceStarted(r.marketStartTime)
    );

    if (activeRaces.length === 0 && results.length > 0) {
      setEvHistory([]);
      setLiquidityHistory([]);
      setBackOddsHistory([]);
      return;
    }

    if (activeRaces.length > 0) {
      const currentLayEV = calculateEVLogic(
        getTrueOddsLogic(activeRaces, currentInstance.racesToWin),
        currentInstance.odds
      );
      const currentMidpointEV = calculateEVLogic(
        getMidpointOddsLogic(activeRaces, currentInstance.racesToWin),
        currentInstance.odds
      );
      const { total: totalLiquidity, value: liquidityRating } =
        calculateTotalLiquidity(activeRaces);

      setEvHistory((prev) => [
        ...prev.slice(-19),
        {
          timestamp: now,
          layEV: currentLayEV ? Math.round(currentLayEV * 100) : null,
          midpointEV: currentMidpointEV
            ? Math.round(currentMidpointEV * 100)
            : null,
        },
      ]);
      setLiquidityHistory((prev) => [
        ...prev.slice(-19),
        { timestamp: now, total: totalLiquidity, rating: liquidityRating },
      ]);

      if (applyBackOddsPercentage) {
        const adjustedOdds = getAdjustedBackOddsLogic(
          activeRaces,
          currentInstance.racesToWin,
          backOddsPercentage,
          applyBackOddsPercentage
        );
        const adjustedEV = adjustedOdds
          ? calculateEVLogic(adjustedOdds, currentInstance.odds)
          : null;
        const adjustedEVPercentageValue = adjustedEV
          ? Math.round(adjustedEV * 100)
          : null;
        if (adjustedEVPercentageValue !== null) {
          setBackOddsHistory((prev) => [
            ...prev.slice(-19),
            { timestamp: now, adjustedEV: adjustedEVPercentageValue },
          ]);
        } else {
          setBackOddsHistory((prev) => [
            ...prev.slice(-19),
            { timestamp: now, adjustedEV: null },
          ]);
        }
      } else {
        setBackOddsHistory([]);
      }
    }
  }, [
    refreshCount,
    activeSessionId,
    results,
    currentInstance,
    applyBackOddsPercentage,
    backOddsPercentage,
    calculateTotalLiquidity,
  ]);

  useEffect(() => {
    if (activeSessionId === null) {
      setEvHistory([]);
      setLiquidityHistory([]);
      setBackOddsHistory([]);
    }
  }, [
    jockey1,
    jockey2,
    boostedOdds,
    racesToWin,
    applyBackOddsPercentage,
    backOddsPercentage,
    activeSessionId,
  ]);

  const saveCurrentSession = () => {
    if (savedSessions.length >= 10) {
      setError('Maximum number of saved sessions (10) reached');
      return;
    }
    if (!currentInstance || !results.length) {
      setError('No current search data or results to save.');
      return;
    }
    const sessionId = generateSessionId();
    const sessionData: SessionData = {
      id: sessionId,
      jockey1,
      jockey2,
      boostedOdds,
      racesToWin,
      results: [...results],
      evHistory: [...evHistory],
      liquidityHistory: [...liquidityHistory],
      backOddsHistory: [...backOddsHistory],
      currentInstance: { ...currentInstance },
      isLive: toggleMode !== 'static',
      lastUpdateTime: lastUpdateTime || new Date(),
      applyBackOddsPercentage,
      backOddsPercentage,
    };
    zustandAddSession(sessionData);
    startNewSearch();
    setHasSearchResults(false);
    setIsStartingNewSearch(false);
  };

  const toggleSessionVisibility = (sessionId: string) => {
    // Exit "New Search mode" when interacting with saved sessions
    setIsStartingNewSearch(false);

    zustandToggleSessionVisibility(sessionId);
  };

  const toggleAllSessions = () => {
    // Exit "New Search mode" when interacting with saved sessions
    setIsStartingNewSearch(false);

    if (zustandVisibleSessionIds.length === savedSessions.length) {
      // Hide all sessions
      zustandVisibleSessionIds.forEach((sessionId) => {
        zustandToggleSessionVisibility(sessionId);
      });
    } else {
      // Show all sessions
      savedSessions.forEach((session) => {
        if (!zustandVisibleSessionIds.includes(session.id)) {
          zustandToggleSessionVisibility(session.id);
        }
      });
    }
  };

  const switchToSession = (sessionId: string) => {
    const session = savedSessions.find((s) => s.id === sessionId);
    if (!session || !session.currentInstance) return;

    // Exit "New Search mode" when interacting with saved sessions
    setIsStartingNewSearch(false);

    // Check visibility first, not active status
    if (zustandVisibleSessionIds.includes(sessionId)) {
      // Session is visible, so hide it
      zustandToggleSessionVisibility(sessionId);
      // If this was the active session, clear the active session
      if (sessionId === activeSessionId) {
        setActiveSessionId(null);
        setHasSearchResults(false);
      }
      return;
    }

    // Session is hidden, so show it and make it active
    setActiveSessionId(sessionId);

    setJockey1(session.jockey1);
    setJockey2(session.jockey2);
    setBoostedOdds(session.boostedOdds);
    setRacesToWin(session.racesToWin);
    setResults(session.results);
    setEvHistory(session.evHistory || []);
    setLiquidityHistory(session.liquidityHistory || []);
    setBackOddsHistory(session.backOddsHistory || []);
    setCurrentInstance(session.currentInstance);
    setLastUpdateTime(session.lastUpdateTime);
    setApplyBackOddsPercentage(session.applyBackOddsPercentage);
    setBackOddsPercentage(session.backOddsPercentage);
    setToggleMode(session.isLive ? 'auto' : 'static');
    setHasSearchResults(true);
    setError('');
    setLoading(false);

    // Make sure it's visible
    if (!zustandVisibleSessionIds.includes(sessionId)) {
      zustandToggleSessionVisibility(sessionId);
    }
  };

  const removeFromWatchlist = (sessionId: string) => {
    zustandRemoveSession(sessionId);
    if (sessionId === activeSessionId) {
      startNewSearch();
    }
  };

  const startNewSearch = () => {
    setJockey1('');
    setJockey2('');
    setBoostedOdds('');
    setRacesToWin(2);
    setApplyBackOddsPercentage(false);
    setBackOddsPercentage(15);
    setResults([]);
    setEvHistory([]);
    setLiquidityHistory([]);
    setBackOddsHistory([]);
    setActiveSessionId(null);
    setHasSearchResults(false);
    setError('');
    setLoading(false);
    setToggleMode('auto');
    setIsStartingNewSearch(true);

    // Hide all saved sessions (Zustand will handle this automatically when removing visibility)
  };

  // Admin console handlers
  const handleAdminAccess = () => {
    setAdminMode('login');
  };

  const handleAdminLoginSuccess = () => {
    setAdminMode('console');
  };

  const handleAdminLogout = () => {
    setAdminMode('none');
    localStorage.removeItem('adminSessionId');
  };

  const handleBackToMainApp = () => {
    setAdminMode('none');
  };

  const updateSavedSession = useCallback(
    (sessionId: string, updateData: Partial<SessionData>) => {
      zustandUpdateSession(sessionId, updateData);
    },
    []
  );

  const memoizedProcessRaceData = useCallback((): {
    eventGroups: Record<string, ProcessedRaceRow[]>;
    allEvents: EventGroup[];
  } => {
    const sourceResults = activeSessionId
      ? savedSessions.find((s) => s.id === activeSessionId)?.results || []
      : results;

    if (!sourceResults.length) return { eventGroups: {}, allEvents: [] };

    const races: Record<string, any> = {};
    sourceResults.forEach((row) => {
      if (hasRaceStarted(row.marketStartTime)) return;
      let raceNum = row.raceNumber;
      if (!raceNum && row.market) {
        const match = row.market.match(/R(\d+)/i);
        if (match) raceNum = match[1];
      }
      if (!raceNum) return;
      const key = `${row.event}|${raceNum}`;
      if (!races[key]) {
        races[key] = {
          event: row.event,
          location: extractLocation(row.event),
          raceNumber: raceNum,
          rows: [],
          isSharedRace: false,
          combinedBackOdds: null,
          combinedLayOdds: null,
          marketStartTime: row.marketStartTime,
          liquidity: 0,
        };
      }
      races[key].rows.push(row);
      races[key].liquidity += row.liquidity || 0;
      if (
        new Date(row.marketStartTime) < new Date(races[key].marketStartTime)
      ) {
        races[key].marketStartTime = row.marketStartTime;
      }
    });

    // Process each race group
    Object.keys(races).forEach((key) => {
      const race = races[key];
      if (race.rows.length > 1) {
        race.isSharedRace = true;
        const backs = race.rows
          .map((r: RaceResult) => parseFloat(r.backOdds))
          .filter((o: number) => !isNaN(o) && o > 0);
        const lays = race.rows
          .map((r: RaceResult) => parseFloat(r.layOdds))
          .filter((o: number) => !isNaN(o) && o > 0);

        const localCalcCombinedOdds = (oddsArr: number[]): number | null => {
          if (oddsArr.length === 0) return null;
          if (oddsArr.length === 1) return oddsArr[0];
          const invSum = oddsArr.reduce((sum, o) => sum + 1 / o, 0);
          return invSum > 0 ? 1 / invSum : null;
        };

        race.combinedBackOdds =
          localCalcCombinedOdds(backs)?.toFixed(2) ?? null;
        race.combinedLayOdds = localCalcCombinedOdds(lays)?.toFixed(2) ?? null;

        // Sort rows within the race by back odds (lowest first)
        race.rows.sort((a: RaceResult, b: RaceResult) => {
          const aOdds = parseFloat(a.backOdds);
          const bOdds = parseFloat(b.backOdds);
          return aOdds - bOdds;
        });
      }
    });

    const eventGroups: Record<string, ProcessedRaceRow[]> = {};
    Object.values(races).forEach((race: any) => {
      const location = race.location;
      if (!eventGroups[location]) eventGroups[location] = [];

      // Process rows after sorting
      const processedRows: ProcessedRaceRow[] = race.rows.map(
        (row: RaceResult, index: number): ProcessedRaceRow => ({
          ...row,
          isSharedRace: race.isSharedRace,
          raceNumber: race.raceNumber,
          liquidityValue: row.liquidity,
          isFirstInSharedRace: race.isSharedRace && index === 0,
          sharedRaceRowCount:
            race.isSharedRace && index === 0 ? race.rows.length : undefined,
          combinedBackOdds:
            race.isSharedRace && index === 0 ? race.combinedBackOdds : null,
          combinedLayOdds:
            race.isSharedRace && index === 0 ? race.combinedLayOdds : null,
        })
      );
      eventGroups[location].push(...processedRows);
    });

    // Sort event groups by earliest race time
    const allEventsArray: EventGroup[] = Object.entries(eventGroups)
      .map(([location, eventRows]) => ({ location, rows: eventRows }))
      .sort((groupA, groupB) => {
        const earliestA = Math.min(
          ...groupA.rows.map((r) => new Date(r.marketStartTime).getTime())
        );
        const earliestB = Math.min(
          ...groupB.rows.map((r) => new Date(r.marketStartTime).getTime())
        );
        if (earliestA !== earliestB) return earliestA - earliestB;
        return groupA.location.localeCompare(groupB.location);
      });

    // Sort rows within each group by time and race number
    allEventsArray.forEach((group) => {
      group.rows.sort((a, b) => {
        // First sort by time
        const timeDiff =
          new Date(a.marketStartTime).getTime() -
          new Date(b.marketStartTime).getTime();
        if (timeDiff !== 0) return timeDiff;
        // Then by race number
        const raceNumDiff = parseInt(a.raceNumber) - parseInt(b.raceNumber);
        if (raceNumDiff !== 0) return raceNumDiff;
        // Finally by back odds for shared races
        if (a.isSharedRace && b.isSharedRace) {
          return parseFloat(a.backOdds) - parseFloat(b.backOdds);
        }
        return 0;
      });
    });

    return { eventGroups, allEvents: allEventsArray };
  }, [results, activeSessionId, savedSessions]);

  const { allEvents } = memoizedProcessRaceData();

  // Determine which results and history to show in the main display area
  const displayResults = activeSessionId
    ? savedSessions.find((s) => s.id === activeSessionId)?.results || []
    : results;
  const displayEVHistory = activeSessionId
    ? savedSessions.find((s) => s.id === activeSessionId)?.evHistory || []
    : evHistory;
  const displayLiquidityHistory = activeSessionId
    ? savedSessions.find((s) => s.id === activeSessionId)?.liquidityHistory ||
      []
    : liquidityHistory;

  const displayApplyBackOddsPercentage = activeSessionId
    ? savedSessions.find((s) => s.id === activeSessionId)
        ?.applyBackOddsPercentage || false
    : applyBackOddsPercentage;
  const displayBackOddsPercentageValue = activeSessionId
    ? savedSessions.find((s) => s.id === activeSessionId)?.backOddsPercentage ||
      15
    : backOddsPercentage;
  const displayBoostedOdds = activeSessionId
    ? savedSessions.find((s) => s.id === activeSessionId)?.boostedOdds || ''
    : boostedOdds;
  const displayRacesToWin = activeSessionId
    ? savedSessions.find((s) => s.id === activeSessionId)?.racesToWin ||
      racesToWin
    : racesToWin;

  const activeDisplayResults = displayResults.filter(
    (r) => !hasRaceStarted(r.marketStartTime)
  );

  const mainDisplayLayTrueOdds = getTrueOddsForMainDisplay(
    activeDisplayResults,
    displayRacesToWin
  );
  const mainDisplayLayTrueOddsFormatted = mainDisplayLayTrueOdds
    ? mainDisplayLayTrueOdds.toFixed(2)
    : 'N/A';

  const mainDisplayMidpointOdds = getMidpointOddsForMainDisplay(
    activeDisplayResults,
    displayRacesToWin
  );
  const mainDisplayMidpointFormatted = mainDisplayMidpointOdds
    ? mainDisplayMidpointOdds.toFixed(2)
    : 'N/A';

  const mainDisplayParsedBoostedOddsNum = parseFloat(displayBoostedOdds);

  const mainDisplayLayEV =
    mainDisplayLayTrueOdds && !isNaN(mainDisplayParsedBoostedOddsNum)
      ? calculateEVLogic(
          mainDisplayLayTrueOdds,
          mainDisplayParsedBoostedOddsNum
        )
      : null;
  const mainDisplayLayEVPercentage = mainDisplayLayEV
    ? Math.round(mainDisplayLayEV * 100)
    : null;
  const mainDisplayLayEVClass =
    mainDisplayLayEV !== null && mainDisplayLayEV > 0
      ? 'positive'
      : mainDisplayLayEV !== null && mainDisplayLayEV < 0
        ? 'negative'
        : '';

  const mainDisplayMidpointEV =
    mainDisplayMidpointOdds && !isNaN(mainDisplayParsedBoostedOddsNum)
      ? calculateEVLogic(
          mainDisplayMidpointOdds,
          mainDisplayParsedBoostedOddsNum
        )
      : null;
  const mainDisplayMidpointEVPercentage = mainDisplayMidpointEV
    ? Math.round(mainDisplayMidpointEV * 100)
    : null;
  const mainDisplayMidpointEVClass =
    mainDisplayMidpointEV !== null && mainDisplayMidpointEV > 0
      ? 'positive'
      : mainDisplayMidpointEV !== null && mainDisplayMidpointEV < 0
        ? 'negative'
        : '';

  const mainDisplayAdjustedBackOdds = displayApplyBackOddsPercentage
    ? getAdjustedBackOddsForMainDisplay(
        activeDisplayResults,
        displayRacesToWin,
        displayBackOddsPercentageValue,
        displayApplyBackOddsPercentage
      )
    : null;
  const mainDisplayAdjustedBackOddsFormatted = mainDisplayAdjustedBackOdds
    ? mainDisplayAdjustedBackOdds.toFixed(2)
    : 'N/A';

  const mainDisplayAdjustedBackEV =
    mainDisplayAdjustedBackOdds && !isNaN(mainDisplayParsedBoostedOddsNum)
      ? calculateEVLogic(
          mainDisplayAdjustedBackOdds,
          mainDisplayParsedBoostedOddsNum
        )
      : null;
  const mainDisplayAdjustedBackEVPercentage = mainDisplayAdjustedBackEV
    ? Math.round(mainDisplayAdjustedBackEV * 100)
    : null;
  const mainDisplayAdjustedBackEVClass =
    mainDisplayAdjustedBackEV !== null && mainDisplayAdjustedBackEV > 0
      ? 'positive'
      : mainDisplayAdjustedBackEV !== null && mainDisplayAdjustedBackEV < 0
        ? 'negative'
        : '';

  const {
    symbol: mainDisplayLiquiditySymbol,
    total: mainDisplayTotalLiquidity,
  } = calculateTotalLiquidity(activeDisplayResults);

  const mainProcessedEVHistoryData = processTimeSeriesData(displayEVHistory);
  const mainProcessedLiquidityHistoryData = processTimeSeriesData(
    displayLiquidityHistory
  );

  // Determine if the current sport/evaluation combination is the implemented one
  const isImplementedFeatureSelected =
    selectedSportType === 'horse-racing' && selectedEvaluationType === 'jockey';

  useEffect(() => {
    if (savedSessions.length === 0 && !hasSearchResults && !activeSessionId) {
      setIsStartingNewSearch(true);
    }
  }, [savedSessions.length, hasSearchResults, activeSessionId]);

  // Reset chart history when boosted odds or races to win change
  useEffect(() => {
    if (hasSearchResults && !activeSessionId) {
      // Clear all chart history when fundamental parameters change
      setEvHistory([]);
      setLiquidityHistory([]);
      setBackOddsHistory([]);
    }
  }, [boostedOdds, racesToWin, hasSearchResults, activeSessionId]);

  // Reset chart history when number of active races changes
  const activeRacesCount = results.length;
  useEffect(() => {
    if (hasSearchResults && !activeSessionId && activeRacesCount > 0) {
      // Clear chart history when races are added/removed (e.g., when races start/end)
      setEvHistory([]);
      setLiquidityHistory([]);
      setBackOddsHistory([]);
    }
  }, [activeRacesCount, hasSearchResults, activeSessionId]);

  return (
    <div className='min-h-screen bg-background-light dark:bg-background-dark relative overflow-hidden'>
      {/* Admin Console Views */}
      {adminMode === 'login' && (
        <AdminAuth
          onSuccess={handleAdminLoginSuccess}
          onBack={handleBackToMainApp}
        />
      )}

      {/* Temporarily disabled AdminConsole for testing - will re-enable once component exists */}
      {adminMode === 'console' && <AdminConsole onLogout={handleAdminLogout} />}
      {/* {adminMode === 'console' && (
        <div className='p-8 text-center'>
          <h2 className='text-xl mb-4'>Admin Console</h2>
          <p className='mb-4'>
            Admin Console temporarily disabled for testing.
          </p>
          <button
            onClick={handleAdminLogout}
            className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600'
          >
            Logout
          </button>
        </div>
      )} */}

      {/* Main Application (only show when not in admin mode) */}
      {adminMode === 'none' && (
        <>
          {showAnimatedBackground && <AnimatedBackground darkMode={darkMode} />}
          {!showAnimatedBackground && (
            <div
              className='fixed inset-0 z-0'
              style={{ backgroundColor: darkMode ? '#141414' : '#C0C0C0' }}
            ></div>
          )}
          <div className='container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10'>
            <div className='py-6'>
              <SystemControlsBar
                darkMode={darkMode}
                onSetDarkMode={setDarkMode}
                toggleMode={toggleMode}
                onSetToggleMode={setToggleMode}
                systemOnline={systemOnline}
                lastUpdateText={lastUpdateText}
                isRefreshing={isRefreshing}
                showAnimatedBackground={showAnimatedBackground}
                onSetShowAnimatedBackground={setShowAnimatedBackground}
                onAdminAccess={handleAdminAccess}
              />

              <div className='hidden sm:block'>
                <SessionNavigationBar
                  hasSearchResults={hasSearchResults}
                  activeSessionId={activeSessionId}
                  savedSessions={savedSessions}
                  visibleSessionIds={zustandVisibleSessionIds}
                  toggleMode={toggleMode}
                  onStartNewSearch={startNewSearch}
                  onSaveCurrentSession={saveCurrentSession}
                  onToggleAllSessions={toggleAllSessions}
                  onSwitchToSession={switchToSession}
                />
              </div>

              {/* Two-column layout for form and results */}
              {!activeSessionId && (
                <>
                  {((savedSessions.length === 0 && !hasSearchResults) ||
                    isStartingNewSearch) && (
                    <div className='mb-6'>
                      <SportAndEvaluationSelection
                        sportTypes={sportTypes}
                        evaluationTypes={evaluationTypes}
                        selectedSportType={selectedSportType}
                        onSelectSportType={selectSportTypeHandler}
                        selectedEvaluationType={selectedEvaluationType}
                        onSelectEvaluationType={selectEvaluationTypeHandler}
                      />
                    </div>
                  )}

                  {/* Responsive layout: vertical on mobile, horizontal on larger screens */}
                  <div className='flex flex-col lg:flex-row gap-6 lg:gap-8 mb-8 items-start'>
                    {/* Search Form - Always visible on mobile, sidebar on desktop */}
                    <div className='w-full lg:w-1/5 lg:flex-shrink-0 space-y-6'>
                      {isImplementedFeatureSelected && (
                        <div className='lg:sticky lg:top-6'>
                          <SearchForm
                            jockey1={jockey1}
                            setJockey1={setJockey1}
                            jockey2={jockey2}
                            setJockey2={setJockey2}
                            boostedOdds={boostedOdds}
                            setBoostedOdds={setBoostedOdds}
                            racesToWin={racesToWin}
                            setRacesToWin={setRacesToWin}
                            applyBackOddsPercentage={applyBackOddsPercentage}
                            setApplyBackOddsPercentage={
                              setApplyBackOddsPercentage
                            }
                            backOddsPercentage={backOddsPercentage}
                            setBackOddsPercentage={setBackOddsPercentage}
                            loading={loading}
                            onSubmit={handleSubmit}
                            darkMode={darkMode}
                          />
                        </div>
                      )}
                    </div>

                    {/* Results Area - Full width on mobile, 4/5 width on desktop */}
                    <div className='w-full lg:w-4/5 lg:flex-grow'>
                      {!hasSearchResults &&
                        !loading &&
                        !activeSessionId &&
                        !zustandVisibleSessionIds.length && (
                          <div className='bg-surface-light/80 dark:bg-surface-dark/80 backdrop-blur-sm rounded-lg border border-border-light dark:border-border-dark p-8 lg:p-12'>
                            <div className='text-center'>
                              <div className='mb-6'>
                                <svg
                                  className='mx-auto h-16 lg:h-20 w-16 lg:w-20 text-text-secondary-light dark:text-text-secondary-dark opacity-50'
                                  fill='none'
                                  viewBox='0 0 24 24'
                                  stroke='currentColor'
                                  strokeWidth={1}
                                >
                                  <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    d='M3 13l4-4 4 4 6-6m0 0l-3-3m3 3l-3 3'
                                  />
                                  <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    d='M3 17h18v2H3z'
                                  />
                                  <circle
                                    cx='7'
                                    cy='9'
                                    r='1.5'
                                    fill='currentColor'
                                  />
                                  <circle
                                    cx='11'
                                    cy='13'
                                    r='1.5'
                                    fill='currentColor'
                                  />
                                  <circle
                                    cx='17'
                                    cy='7'
                                    r='1.5'
                                    fill='currentColor'
                                  />
                                </svg>
                              </div>
                              <h3 className='text-lg lg:text-xl font-semibold text-text-primary-light dark:text-text-primary-dark mb-3'>
                                Ready to Find Your Edge?
                              </h3>
                              {/* Responsive placeholder text */}
                              <p className='text-text-secondary-light dark:text-text-secondary-dark mb-6 max-w-md mx-auto'>
                                <span className='lg:hidden'>
                                  Complete the form above to search for
                                  profitable betting opportunities. Your results
                                  will appear here.
                                </span>
                                <span className='hidden lg:inline'>
                                  Complete the form on the left to search for
                                  profitable betting opportunities. Your results
                                  will appear here.
                                </span>
                              </p>
                              <div className='text-xs lg:text-sm text-text-secondary-light dark:text-text-secondary-dark opacity-75 space-y-2'>
                                <p>• Select your sport and evaluation type</p>
                                <p>• Enter jockey details and odds</p>
                                <p>• Click search to see live data</p>
                              </div>
                            </div>
                          </div>
                        )}

                      {loading && (
                        <div className='bg-surface-light/80 dark:bg-surface-dark/80 backdrop-blur-sm rounded-lg border border-border-light dark:border-border-dark p-8 lg:p-12'>
                          <div className='text-center'>
                            <div className='mb-6'>
                              <svg
                                className='mx-auto h-16 lg:h-20 w-16 lg:w-20 text-text-secondary-light dark:text-text-secondary-dark opacity-50'
                                fill='none'
                                viewBox='0 0 24 24'
                                stroke='currentColor'
                                strokeWidth={1}
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  d='M3 13l4-4 4 4 6-6m0 0l-3-3m3 3l-3 3'
                                />
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  d='M3 17h18v2H3z'
                                />
                                <circle
                                  cx='7'
                                  cy='9'
                                  r='1.5'
                                  fill='currentColor'
                                />
                                <circle
                                  cx='11'
                                  cy='13'
                                  r='1.5'
                                  fill='currentColor'
                                />
                                <circle
                                  cx='17'
                                  cy='7'
                                  r='1.5'
                                  fill='currentColor'
                                />
                              </svg>
                            </div>
                            <div
                              className='text-lg lg:text-xl font-semibold bg-gradient-to-r from-[#2A7B9B] via-[#57C785] via-[#EDDD53] to-[#FFFFFF] bg-clip-text text-transparent animate-pulse'
                              style={{
                                backgroundSize: '200% 100%',
                                animation:
                                  'gradient-shift 3s ease-in-out infinite',
                              }}
                              dangerouslySetInnerHTML={{
                                __html: loadingMessage,
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {hasSearchResults &&
                        !loading &&
                        isImplementedFeatureSelected &&
                        !activeSessionId && (
                          <ContentContainer
                            isLive={isLive}
                            toggleMode={toggleMode}
                            timeSinceUpdate={timeSinceUpdate}
                            refreshCycleSeconds={refreshCycleSeconds.current}
                            isRefreshing={isRefreshing}
                          >
                            {displayApplyBackOddsPercentage &&
                              mainDisplayAdjustedBackOddsFormatted &&
                              mainDisplayAdjustedBackOddsFormatted !==
                                'N/A' && (
                                <div className='bg-surface-light/80 dark:bg-surface-dark/80 backdrop-blur-sm rounded-lg shadow-lg p-4 border border-border-light dark:border-border-dark'>
                                  <span className='text-text-secondary-light dark:text-text-secondary-dark'>
                                    Back Odds +{displayBackOddsPercentageValue}
                                    %:
                                  </span>
                                  <span className='ml-2 font-semibold text-text-primary-light dark:text-text-primary-dark'>
                                    {mainDisplayAdjustedBackOddsFormatted}
                                  </span>
                                  {mainDisplayAdjustedBackEVPercentage !==
                                    null && (
                                    <span className='ml-2 text-text-secondary-light dark:text-text-secondary-dark'>
                                      (
                                      {mainDisplayAdjustedBackEVPercentage > 0
                                        ? '+'
                                        : ''}
                                      {mainDisplayAdjustedBackEVPercentage}%)
                                    </span>
                                  )}
                                </div>
                              )}

                            <div className='flex gap-4 items-end'>
                              <div className='flex-1'>
                                <label className='block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-1'>
                                  Boosted Odds
                                </label>
                                <input
                                  type='number'
                                  value={boostedOdds}
                                  onChange={(e) =>
                                    setBoostedOdds(e.target.value)
                                  }
                                  placeholder='e.g., 2.80'
                                  step='0.01'
                                  min='1'
                                  inputMode='decimal'
                                  className='block w-full rounded-md border-0 py-2.5 px-3.5 text-sm leading-6 shadow-sm ring-1 ring-inset ring-border-light focus:ring-1 focus:ring-inset focus:ring-border-light bg-surface-light text-text-primary-light dark:bg-surface-darker dark:text-text-primary-dark dark:ring-input-border-dark dark:focus:ring-input-border-dark'
                                />
                              </div>
                              <div className='flex-1'>
                                <label className='block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-1'>
                                  Races to Win: {racesToWin}
                                </label>
                                <input
                                  type='range'
                                  min='1'
                                  max='5'
                                  value={racesToWin}
                                  onChange={(e) =>
                                    setRacesToWin(parseInt(e.target.value, 10))
                                  }
                                  className='w-full h-2 bg-surface-light dark:bg-surface-dark rounded-lg appearance-none cursor-pointer accent-white dark:accent-black'
                                />
                              </div>
                            </div>

                            <ResultsTable
                              allEvents={allEvents}
                              isLive={isLive && toggleMode !== 'static'}
                              toggleMode={toggleMode}
                            />

                            <MetricsDisplay
                              layOddsValue={mainDisplayLayTrueOddsFormatted}
                              layEVPercentage={mainDisplayLayEVPercentage}
                              layEVClass={mainDisplayLayEVClass}
                              midpointOddsValue={mainDisplayMidpointFormatted}
                              midpointEVPercentage={
                                mainDisplayMidpointEVPercentage
                              }
                              midpointEVClass={mainDisplayMidpointEVClass}
                              totalLiquidityValue={`$${Math.floor(mainDisplayTotalLiquidity).toLocaleString()}`}
                              liquiditySymbol={mainDisplayLiquiditySymbol}
                              adjustedBackOddsValue={
                                mainDisplayAdjustedBackOddsFormatted !== 'N/A'
                                  ? mainDisplayAdjustedBackOddsFormatted
                                  : undefined
                              }
                              adjustedBackEVPercentage={
                                mainDisplayAdjustedBackEVPercentage
                              }
                              adjustedBackEVClass={
                                mainDisplayAdjustedBackEVClass
                              }
                              applyBackOddsPercentage={
                                displayApplyBackOddsPercentage
                              }
                              backOddsPercentageValue={
                                displayBackOddsPercentageValue
                              }
                              evHistory={mainProcessedEVHistoryData.data}
                              liquidityHistory={
                                mainProcessedLiquidityHistoryData.data
                              }
                              evTimeScale={mainProcessedEVHistoryData.timeScale}
                              liquidityTimeScale={
                                mainProcessedLiquidityHistoryData.timeScale
                              }
                              isLive={isLive && toggleMode !== 'static'}
                              areChartsVisible={true}
                              baseChartId={
                                activeSessionId
                                  ? `session-${activeSessionId}-main`
                                  : 'main-search'
                              }
                              isLoading={loading}
                            />

                            <div className='flex justify-between items-center'>
                              <button
                                onClick={saveCurrentSession}
                                className='w-full px-6 py-3 text-base font-semibold text-[#2A7B9B] bg-transparent hover:bg-[#2A7B9B]/10 rounded-lg border border-[#2A7B9B]/50 backdrop-blur-sm transition-all duration-200 hover:shadow-lg hover:border-[#2A7B9B]'
                              >
                                Add to Watchlist
                              </button>
                            </div>
                          </ContentContainer>
                        )}
                    </div>
                  </div>
                </>
              )}

              {error && (
                <div className='mb-8'>
                  <div className='bg-red-50 dark:bg-red-900/20 border border-red-400 dark:border-red-600 rounded-lg p-4'>
                    <p className='text-red-700 dark:text-red-400'>{error}</p>
                  </div>
                </div>
              )}

              {savedSessions.length > 0 && (
                <div className='space-y-6'>
                  {savedSessions.map((session) => (
                    <SavedSessionCard
                      key={session.id}
                      sessionData={session}
                      isVisible={zustandVisibleSessionIds.includes(session.id)}
                      activeSessionId={activeSessionId}
                      toggleMode={toggleMode}
                      timeSinceUpdate={timeSinceUpdate}
                      refreshCycleSeconds={refreshCycleSeconds.current}
                      isRefreshing={isRefreshing}
                      onUpdateSession={updateSavedSession}
                      onRemoveSession={removeFromWatchlist}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* iOS Install Prompt */}
      {showIOSPrompt && <IOSInstallPrompt onClose={hideIOSPrompt} />}
    </div>
  );
}

export default App;
